/**
 * Badge Service
 * 
 * Handles all badge-related API calls including:
 * - CRUD operations for badges
 * - Badge authorization and issuing
 * - Default badges management
 * - Badge requests and approvals
 * - Member badge assignments
 */

import api from './api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Badge {
  _id?: string;
  name?: string;
  description?: string;
  [key: string]: any;
}

export interface BadgeFilters {
  [key: string]: any;
}

// ============================================================================
// Badge CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/badge
 * Auth: no
 * Gets all badges
 */
export async function getBadges(params?: BadgeFilters): Promise<any> {
  return api.get('/badge', { params });
}

/**
 * API: GET /api/v2.0/badge/:id
 * Auth: no
 * Gets a specific badge by ID
 */
export async function getBadgeById(id: string): Promise<any> {
  return api.get(`/badge/${id}`);
}

/**
 * API: POST /api/v2.0/badge
 * Auth: no
 * Creates a new badge
 */
export async function createBadge(data: Badge): Promise<any> {
  return api.post('/badge', data);
}

/**
 * API: POST /api/v2.0/update/badge/:id
 * Auth: no
 * Updates an existing badge
 */
export async function updateBadge(id: string, data: Badge): Promise<any> {
  return api.post(`/update/badge/${id}`, data);
}

/**
 * API: POST /api/v2.0/cloneBadge
 * Auth: no
 * Clones an existing badge
 */
export async function cloneBadge(data: { badgeId: string; [key: string]: any }): Promise<any> {
  return api.post('/cloneBadge', data);
}

// ============================================================================
// Badge Filtering & Studio
// ============================================================================

/**
 * API: GET /api/v2.0/badgefiltered
 * Auth: no
 * Gets filtered badges based on criteria
 */
export async function getFilteredBadges(params?: BadgeFilters): Promise<any> {
  return api.get('/badgefiltered', { params });
}

/**
 * API: GET /api/v2.0/badgestudio/:id
 * Auth: no
 * Gets badge studio information for a specific badge
 */
export async function getBadgeStudio(id: string): Promise<any> {
  return api.get(`/badgestudio/${id}`);
}

// ============================================================================
// Default Badges Management
// ============================================================================

/**
 * API: GET /api/v2.0/getDefaultBadges
 * Auth: no
 * Gets all default badges
 */
export async function getDefaultBadges(): Promise<any> {
  return api.get('/getDefaultBadges');
}

/**
 * API: GET /api/v2.0/getDefaultBadgesPaged
 * Auth: no
 * Gets default badges with pagination
 */
export async function getDefaultBadgesPaged(params?: { page?: number; limit?: number }): Promise<any> {
  return api.get('/getDefaultBadgesPaged', { params });
}

/**
 * API: POST /api/v2.0/setDefaultBadges
 * Auth: no
 * Sets the default badges collection
 */
export async function setDefaultBadges(data: { badgeIds: string[] }): Promise<any> {
  return api.post('/setDefaultBadges', data);
}

/**
 * API: POST /api/v2.0/addBadgeToDefaultBadges
 * Auth: no
 * Adds a badge to the default badges collection
 */
export async function addBadgeToDefaultBadges(data: { badgeId: string }): Promise<any> {
  return api.post('/addBadgeToDefaultBadges', data);
}

/**
 * API: POST /api/v2.0/removeBadgeFromDefaultBadges
 * Auth: no
 * Removes a badge from the default badges collection
 */
export async function removeBadgeFromDefaultBadges(data: { badgeId: string }): Promise<any> {
  return api.post('/removeBadgeFromDefaultBadges', data);
}

/**
 * API: GET /api/v2.0/isDefaultBadge
 * Auth: no
 * Checks if a badge is a default badge
 */
export async function isDefaultBadge(params: { badgeId: string }): Promise<any> {
  return api.get('/isDefaultBadge', { params });
}

// ============================================================================
// Badge Authorization & Issuing
// ============================================================================

/**
 * API: GET /api/v2.0/getAuthorizedBadgesToEarn
 * Auth: no
 * Gets badges that the user is authorized to earn
 */
export async function getAuthorizedBadgesToEarn(params?: any): Promise<any> {
  return api.get('/getAuthorizedBadgesToEarn', { params });
}

/**
 * API: GET /api/v2.0/getAuthorizedBadgesToEarnNew
 * Auth: yes
 * Gets authorized badges to earn (new version)
 */
export async function getAuthorizedBadgesToEarnNew(params?: any): Promise<any> {
  return api.get('/getAuthorizedBadgesToEarnNew', { params });
}

/**
 * API: GET /api/v2.0/getIsMemberAuthorizedToEarn
 * Auth: no
 * Checks if a member is authorized to earn a specific badge
 */
export async function getIsMemberAuthorizedToEarn(params: { memberId: string; badgeId: string }): Promise<any> {
  return api.get('/getIsMemberAuthorizedToEarn', { params });
}

/**
 * API: GET /api/v2.0/getMembersWhoCanIssueABadge
 * Auth: yes
 * Gets members who can issue a specific badge
 */
export async function getMembersWhoCanIssueABadge(params: { badgeId: string }): Promise<any> {
  return api.get('/getMembersWhoCanIssueABadge', { params: { badge_id: params.badgeId } });
}

/**
 * API: POST /api/v2.0/updateBadgesMemberCanIssue
 * Auth: yes
 * Updates the badges that a member can issue
 */
export async function updateBadgesMemberCanIssue(data: { memberId: string; badgeIds: string[] }): Promise<any> {
  return api.post('/updateBadgesMemberCanIssue', data);
}

// ============================================================================
// Member Badges
// ============================================================================

/**
 * API: GET /api/v2.0/getMemberBadges
 * Auth: yes
 * Gets all badges earned by a member
 */
export async function getMemberBadges(params?: { memberId?: string }): Promise<any> {
  return api.get('/getMemberBadges', { params });
}

/**
 * API: POST /api/v2.0/getBadgesAdminApp
 * Auth: no
 * Gets badges
 */
export async function getBadgesAdminApp(data?: any): Promise<any> {
  return api.post('/getBadgesAdminApp', data);
}

// ============================================================================
// Organization Badge Management
// ============================================================================

/**
 * API: POST /api/v2.0/getCommittersByOrganization
 * Auth: no
 * Gets all committers in an organization
 */
export async function getCommittersByOrganization(data: { organizationId: string }): Promise<any> {
  return api.post('/getCommittersByOrganization', data);
}

/**
 * API: POST /api/v2.0/getIssuersByOrganization
 * Auth: no
 * Gets all badge issuers in an organization
 */
export async function getIssuersByOrganization(data: { organizationId: string }): Promise<any> {
  return api.post('/getIssuersByOrganization', data);
}
